import React, { useEffect, useState } from 'react';
import { Alert, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import cadesplugin from 'crypto-pro-cadesplugin';
import CustomButton from '../../../../Buttons/CustomButton';
import { flexRowGap2 } from '../../../../../constants/lkCadastr/cadastrStorage';
import Selector from '../../../../Selectors/Selector';
import CustomCheckbox from '../../../../CustomCheckbox';
import DropZone from '../../../../File/DropZone';
import ModalWithTitle from '../../../ModalWithTitle';
import { useApi, usePushNotification } from '../../../../../hooks';
import { cadastreReportHistoryRequest } from '../../../../../api/cadastreApi';
import { METHODS_API } from '../../../../../api/api';

export function SigningElectronicSignature({
  open, close, onSendReport, report, setLoading, loading,
}) {
  const [certificates, setCertificates] = useState([]);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [trustedPerson, setTrustedPerson] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const pushNotification = usePushNotification();

  const fileSize = files.reduce((acc, file) => acc + file.size, 0);
  const maxFilesCoef = 10;
  const maxFileCoef = 10;
  const maxFilesSize = maxFilesCoef * 1024 * 1024;
  const maxFileSize = maxFileCoef * 1024 * 1024;

  async function doCertsList() {
    const certsApi = await cadesplugin();
    const newCerts = await certsApi.getCertsList();

    return newCerts;
  }

  const getOwnerFromSubject = (subjectInfo = '', key) => subjectInfo
    .split(', ')
    .find((item) => item.startsWith(`${key}=`))
    ?.replace(`${key}=`, '');

  const getOwnerOfSign = (cert) => {
    const ownerPeriod = cert?.friendlyValidPeriod?.();

    const surname = getOwnerFromSubject(cert?.subjectInfo, 'SN');
    const gName = getOwnerFromSubject(cert?.subjectInfo, 'G');
    const combinedName = [surname, gName].filter(Boolean).join(' ');

    return {
      owner: getOwnerFromSubject(cert?.subjectInfo, 'CN') || combinedName || '',
      from: ownerPeriod.from.ddmmyy,
      to: ownerPeriod.to.ddmmyy,
      serialNumber: cert.serialNumber,
    };
  };

  const getCerts = async () => {
    const formattedCertificates = await doCertsList();
    setCertificates(formattedCertificates.map((cert) => {
      const issuerInfo = cert.friendlyIssuerInfo();
      const ownerInfo = getOwnerOfSign(cert);

      return {
        ...cert,
        label: issuerInfo[0]?.text ? `${issuerInfo[0].text} ${ownerInfo.owner}` : '',
        id: cert.serialNumber,
        value: cert.serialNumber,
      };
    }));
  };

  const { defaultRequest: reportHistoryApi } = useApi({
    request: cadastreReportHistoryRequest,
  });

  const sendProcurationFiles = async () => {
    const formData = new FormData();
    let attorneyResponse = null;

    files.forEach((item) => {
      formData.append('procuration_document[]', item);
    });
    try {
      if (trustedPerson) {
        attorneyResponse = await reportHistoryApi({
          method: METHODS_API.PUT,
          id: report.id,
          data: formData,
          isFormData: true,
          rest: '/file_upload_procuration',
        });
      }
      if (attorneyResponse?.code === 'ERR_NETWORK') {
        throw Error('Не удалось сохранить файлы доверенности');
      }
    } catch (err) {
      setError(err);
      pushNotification({ message: 'Не удалось сохранить файлы доверенности', variant: 'error' });
      console.error(err);
    }
  };

  const signFiles = async () => {
    setLoading(true);

    try {
      const formattedCertificates = await doCertsList();
      if (trustedPerson && !files.length) {
        setError('Вы указали, что являетесь доверенным лицом. Необходимо прикрепить файл доверенности для отправки отчета.');
        setLoading(false);
        return;
      }
      if (fileSize > maxFilesSize) {
        setError(`Размер файлов не должен превышать ${maxFilesSize / 1024 / 1024} МБ`);
        setLoading(false);
        return;
      }

      if (files.some((item) => item.size > maxFileSize)) {
        setError(`Размер одного файла не должен превышать ${maxFilesSize / 1024 / 1024} МБ`);
        setLoading(false);
        return;
      }

      if (!selectedCertificate) {
        setError('Выберите сертификат');
        throw Error('Выберите сертификат');
      }
      if (selectedCertificate && trustedPerson && !files.length) {
        throw Error('Выберите доверенность');
      }

      await reportHistoryApi({
        method: METHODS_API.PUT,
        id: report.id,
        data: { sign_info: getOwnerOfSign(formattedCertificates.find((cert) => cert.serialNumber === selectedCertificate.id)) },
      });

      onSendReport(sendProcurationFiles);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      getCerts();
    }
  }, [open]);

  const getFiles = async (e, dropZoneFiles) => {
    setError('');
    const newFiles = [];

    for await (const file of dropZoneFiles) {
      file.randId = Math.random();
      newFiles.push(file);
    }
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const onDeleteFile = (file) => {
    setFiles((prev) => prev.filter((f) => f.randId !== file.randId));
  };

  return (
    <ModalWithTitle
      open={open}
      mainSx={{ ...flexRowGap2 }}
      close={close}
      containerSx={{ width: '720px' }}
      title="Выбрать сертификат для подписи"
    >
      <Stack direction="column" justifyContent="center" width="100%" gap="16px">
        {error && (
        <Alert severity="error">
          {error}
        </Alert>
        )}
        <Box display="flex" gap={3}>
          <Selector
            label="Сертификат*"
            multiline
            options={certificates}
            value={selectedCertificate}
            onChange={(e, value) => {
              setSelectedCertificate(value);
              setError('');
            }}
          />
        </Box>
        <CustomCheckbox
          value={trustedPerson}
          onChange={() => {
            setTrustedPerson(!trustedPerson);
            setError('');
          }}
          label="Я не являюсь руководителем организации. Подписываю отчет на основании доверенности"
        />
        {trustedPerson && (
        <>
          Загрузка доверенности
          <Alert color="info">
            Поддерживаются файлы форматов: .pdf, .docx, .jpeg, .png., .xml
            <br />
            Максимальный размер файла:
            {' '}
            {maxFileCoef}
            {' '}
            MB.
          </Alert>
          <DropZone
            getFiles={getFiles}
            accept={{
              'application/pdf': ['.pdf'],
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
              'image/jpeg': ['.jpeg'],
              'image/png': ['.png'],
              'application/xml': ['.xml'],
              'text/xml': ['.xml'],
            }}
            maxFiles={Infinity}
            onDelete={onDeleteFile}
            maxSize={maxFileSize}
            withFiles
            files={files}
          />
          <Typography color={fileSize > maxFilesSize ? 'error' : 'rgba(0, 0, 0, 0.4)'}>
            Всего:
            {' '}
            {' '}
            {(fileSize / (1024 * 1024)).toFixed(1)}
            /
            {maxFilesCoef}
            {' '}
            MB
          </Typography>
        </>
        )}
        <Stack direction="row" gap="8px" justifyContent="center">
          <CustomButton
            sx={{ height: '30px' }}
            isLoading={loading}
            disabled={!selectedCertificate || Object.entries(selectedCertificate || {}).length === 0}
            onClick={signFiles}
          >
            Продолжить
          </CustomButton>
          <CustomButton
            sx={{ height: '30px' }}
            color="error"
            isLoading={loading}
            onClick={close}
          >
            Отмена
          </CustomButton>
        </Stack>
      </Stack>
    </ModalWithTitle>
  );
}
